import { useState } from "react";
import { useToken } from "@/ui/hooks";
import { Box, Button, Flex } from "@/ui";
import NavigationBar from "@/components/NavigationBar";
import Icon from "@/components/Icon";
import IconButton from "@/components/IconButton";
import Circle from "@/components/Circle";
import { CameraIconsArr, CameraColorsArr, CameraColors, CameraIcons } from "@/enums/camera";
import { t } from "@/utils";
import type { WizardProps } from "../Wizard";

export default function CameraCustomization({
  next,
  previous,
  state
}: WizardProps<{ cameraColor: CameraColorId; cameraIcon: CameraIconId }>) {
  const [colorId, setColorId] = useState<CameraColorId>(state.cameraColor || "color1");
  const [iconId, setIconId] = useState<CameraIconId>(state.cameraIcon || "video_camera");

  const [primaryColor, outlineColor, backgroundColor] = useToken("colors", [
    "primary",
    "outline-variant",
    "background"
  ]);

  return (
    <Flex grow={1} direction="column" alignItems="center">
      <NavigationBar
        title={t("cameraCustomization.step2.title")}
        onBack={() =>
          previous({
            stateUpdate: {
              cameraColor: colorId,
              cameraIcon: iconId
            }
          })
        }
        props={{ mb: "8" }}
      />
      <Flex
        mt="62"
        bg="on-secondary"
        borderRadius="full"
        w="112px"
        h="112px"
        alignItems="center"
        justifyContent="center"
      >
        <Icon icon={CameraIcons[iconId].icon} size="64px" fill={CameraColors[colorId].value} />
      </Flex>

      <Flex gap="16" mt="40" justifyContent="center" px="48">
        {CameraColorsArr.map(({ id, value }) => {
          const isSelected = id === colorId;
          return (
            <Circle
              bg={value}
              size="48"
              key={id}
              props={{
                onClick: () => setColorId(id),
                as: "button",
                boxShadow: isSelected
                  ? `0px 0px 0px 2px ${backgroundColor}, 0px 0px 0px 4px ${primaryColor}`
                  : undefined,
                transition: "box-shadow 0.2s"
              }}
            />
          );
        })}
      </Flex>

      <Flex flexWrap="wrap" gap="16" mt="32" justifyContent="center" px="48">
        {CameraIconsArr.map(({ id, icon }) => {
          const isSelected = id === iconId;
          return (
            <IconButton
              key={id}
              p="12"
              fill="secondary"
              icon={icon}
              onClick={() => setIconId(id)}
              props={{
                boxShadow: isSelected ? `0px 0px 0px 2px ${primaryColor}` : `0px 0px 0px 1px ${outlineColor}`,
                transition: "box-shadow 0.2s"
              }}
            />
          );
        })}
      </Flex>

      <Box flexGrow={1} />
      <Button
        mt="48"
        bg="primary"
        color="on-primary"
        minW="18.5rem"
        onClick={() =>
          next({
            stateUpdate: {
              cameraColor: colorId,
              cameraIcon: iconId
            }
          })
        }
      >
        {t("buttons.continue")}
      </Button>
    </Flex>
  );
}
